import type { QueryKey } from '@tanstack/react-query';

import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { GetDependencyConfidenceResponse } from '../../../../shared/types/routes/binary';

const getRevisionDependencyConfidenceQueryKey = (
  revisionLocator: string,
  dependencyLocator: string,
): QueryKey => ['binary', revisionLocator, 'dependency-confidence', dependencyLocator];

function getRevisionDependencyConfidence(revisionLocator: string, dependencyLocator: string) {
  return get<GetDependencyConfidenceResponse>(`/api/binary/${nuc(revisionLocator)}/dependency-confidence/${nuc(dependencyLocator)}`);
}

/**
 * Helper hook for fetching the confidence of the dependencies of a user's revision.
 * @param revisionLocator - The revision locator of the user's revision.
 * @param dependencyLocator - The locator of a dependency - if provided will only fetch the confidence for the single dependency.
 * @param shouldFetch - default true, allows hook to be skipped when project not available.
 * @returns A query for the confidence of the revision's dependencies.
 */
export default function useGetRevisionDependencyConfidence(
  revisionLocator: string,
  dependencyLocator: string,
  shouldFetch = true,
) {
  return useQuery<GetDependencyConfidenceResponse>({
    queryFn: () => getRevisionDependencyConfidence(revisionLocator, dependencyLocator),
    queryKey: getRevisionDependencyConfidenceQueryKey(revisionLocator, dependencyLocator),
    enabled: shouldFetch,
  });
}
